import { useAuth, authorizedFetch } from '@yeshuman/sdk'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

export interface InsightRelation {
  id: string
  content_type: string  // 'observation' | 'symptom' | 'activity'
  object_id: string
  content_object: {
    id: string
    text?: string  // For observations
    symptom_type?: string
    anatomy?: string
    severity?: string
    occurred_at?: string
    activity_type?: string
    date?: string
  }
  relation_type: 'related_to' | 'context_for' | 'follows' | 'contradicts' | 'confirms'
  strength: number
  discovery_method: 'agent' | 'llm_analysis' | 'pattern_detection'
  created_at: string
}

export interface Insight {
  id: string
  title: string
  description: string
  status: 'emerging' | 'active' | 'improving' | 'resolved' | 'monitoring'
  confidence: number
  is_exploratory: boolean
  start_date?: string
  end_date?: string
  thread_id?: string
  message_id?: string
  source_message_text?: string
  relations: InsightRelation[]
  created_at: string
  updated_at: string
}

export interface InsightFilters {
  limit?: number
  status?: string
  start_date_from?: string
  start_date_to?: string
  confidence_min?: number
  thread_id?: string
}

export interface CreateInsightData {
  title: string
  description: string
  status?: string
  confidence?: number
  start_date?: string
  end_date?: string
  related_observation_ids?: string[]
  related_symptom_ids?: string[]
  related_activity_ids?: string[]
}

export interface UpdateInsightData {
  insight_id: string
  title?: string
  description?: string
  status?: string
  confidence?: number
  start_date?: string
  end_date?: string
  related_observation_ids?: string[]
  related_symptom_ids?: string[]
  related_activity_ids?: string[]
}

export function useInsights(filters: InsightFilters = {}) {
  const { token } = useAuth()

  const queryKey = ['insights', filters]

  const insightsQuery = useQuery({
    queryKey,
    queryFn: async (): Promise<Insight[]> => {
      if (!token) throw new Error('Not authenticated')

      const params = new URLSearchParams()
      if (filters.status) params.append('status', filters.status)
      if (filters.start_date_from) params.append('start_date_from', filters.start_date_from)
      if (filters.start_date_to) params.append('start_date_to', filters.start_date_to)
      if (filters.confidence_min !== undefined) params.append('confidence_min', filters.confidence_min.toString())
      if (filters.thread_id) params.append('thread_id', filters.thread_id)
      if (filters.limit) params.append('limit', filters.limit.toString())

      const url = `/api/insights/${params.toString() ? '?' + params.toString() : ''}`

      const response = await authorizedFetch(url)

      if (!response.ok) {
        throw new Error('Failed to fetch insights')
      }

      return response.json()
    },
    enabled: !!token,
    staleTime: 30 * 1000, // 30 seconds
  })

  return {
    insights: insightsQuery.data || [],
    isLoading: insightsQuery.isLoading,
    error: insightsQuery.error,
    refetch: insightsQuery.refetch,
  }
}

export function useInsight(insightId: string | null) {
  const { token } = useAuth()

  const insightQuery = useQuery({
    queryKey: ['insights', insightId],
    queryFn: async (): Promise<Insight> => {
      if (!token || !insightId) throw new Error('Not authenticated or no insight ID')

      const response = await authorizedFetch(`/api/insights/${insightId}`)

      if (!response.ok) {
        throw new Error('Failed to fetch insight')
      }

      return response.json()
    },
    enabled: !!token && !!insightId,
  })

  return {
    insight: insightQuery.data,
    isLoading: insightQuery.isLoading,
    error: insightQuery.error,
  }
}

export function useCreateInsight() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateInsightData): Promise<Insight> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch('/api/insights/', {
        method: 'POST',
        body: JSON.stringify(data),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || errorData.error || 'Failed to create insight')
      }

      return response.json()
    },
    onSuccess: () => {
      // Invalidate insights list cache
      queryClient.invalidateQueries({ queryKey: ['insights'] })
    },
  })
}

export function useUpdateInsight() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: UpdateInsightData): Promise<Insight> => {
      if (!token) throw new Error('Not authenticated')

      const { insight_id, ...updateData } = data

      const response = await authorizedFetch(`/api/insights/${insight_id}`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || errorData.error || 'Failed to update insight')
      }

      return response.json()
    },
    onSuccess: (data) => {
      // Invalidate insights list and specific insight cache
      queryClient.invalidateQueries({ queryKey: ['insights'] })
      queryClient.invalidateQueries({ queryKey: ['insights', data.id] })
    },
  })
}

export function useDeleteInsight() {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (insightId: string): Promise<void> => {
      if (!token) throw new Error('Not authenticated')

      const response = await authorizedFetch(`/api/insights/${insightId}`, {
        method: 'DELETE',
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.detail || errorData.error || 'Failed to delete insight')
      }
    },
    onSuccess: () => {
      // Invalidate insights list cache
      queryClient.invalidateQueries({ queryKey: ['insights'] })
    },
  })
}
